import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  fetchBillingInvoice,
  fetchBillingInvoices,
  fetchBillingPayment,
  fetchBillingPayments,
  fetchBillingPlans,
  fetchClientBilling,
  fetchBillingSummary,
  fetchCollectedDailyReport,
  fetchCollectedReport,
  type InvoiceSortKey,
  type LedgerSort,
  type PaymentSortKey,
} from "../api/billing";

// The firm-wide reads are the admin's: `enabled` keeps a page that will
// not show them from asking.
export function useBillingSummary(enabled: boolean) {
  return useQuery({
    queryKey: ["billing", "summary"],
    queryFn: fetchBillingSummary,
    staleTime: 60_000,
    enabled,
  });
}

export function useBillingInvoices(sort: LedgerSort<InvoiceSortKey>, enabled: boolean) {
  return useQuery({
    queryKey: ["billing", "invoices", sort.key, sort.dir],
    queryFn: () => fetchBillingInvoices(sort),
    staleTime: 60_000,
    // Re-sorting must not blank the table while the new order loads.
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useBillingPayments(sort: LedgerSort<PaymentSortKey>, enabled: boolean) {
  return useQuery({
    queryKey: ["billing", "payments", sort.key, sort.dir],
    queryFn: () => fetchBillingPayments(sort),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useBillingPlans(enabled: boolean) {
  return useQuery({
    queryKey: ["billing", "plans"],
    queryFn: fetchBillingPlans,
    staleTime: 60_000,
    enabled,
  });
}

// Under the ["billing"] prefix on purpose: every money write invalidates
// that prefix, so a payment recorded anywhere refreshes the client card.
export function useClientBilling(clientId: string, enabled = true) {
  return useQuery({
    queryKey: ["billing", "client", clientId],
    queryFn: () => fetchClientBilling(clientId),
    staleTime: 60_000,
    enabled,
  });
}

// Deliberately no keepPreviousData: the sub-header names the picked range
// the moment it changes, so last range's rows must never sit under it -
// a skeleton, an error, or the new numbers, nothing else.
export function useCollectedReport(from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: ["billing", "report", from, to],
    queryFn: () => fetchCollectedReport(from, to),
    staleTime: 60_000,
    enabled,
  });
}

// Same no-stale-rows rule; the day buckets feed the dashboard's bars.
export function useCollectedDailyReport(from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: ["billing", "report", "day", from, to],
    queryFn: () => fetchCollectedDailyReport(from, to),
    staleTime: 60_000,
    enabled,
  });
}

export function useBillingInvoice(id: string | undefined) {
  return useQuery({
    queryKey: ["billing", "invoice", id ?? ""],
    queryFn: () => fetchBillingInvoice(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useBillingPayment(id: string | undefined) {
  return useQuery({
    queryKey: ["billing", "payment", id ?? ""],
    queryFn: () => fetchBillingPayment(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}
